<template>
    <PlanbordGridRow v-if="titleBeforeResource" :data-id="titleBeforeResource.id" :data-rank="titleBeforeResource.rankInRoster">
        <div
            class="roster-title"
            :class="{editable: allowedToReorderUsers}"
            @click="openTitleEditModal"
            :draggable="allowedToReorderUsers"
            @dragstart="handleDragDropEvents"
            @dragend="handleDragDropEvents"
            @dragover.prevent.stop="handleDragDropEvents"
            @dragleave.prevent="handleDragDropEvents"
            @drop.prevent.stop="handleDragDropEvents"
        >
            <span>{{ titleBeforeResource.title }}</span>
        </div>
    </PlanbordGridRow>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import TitleEditModal from '@/components/modals/TitleEditModal.vue';
    import {MoveTitleRankData, MoveResourceRankData} from '@/types';
    import {ResourceRosterRankUtil} from '@/util/ResourceRosterRankUtil';
    import {Title} from '@cquential/api';
    import {UserPermissions} from '@/types/enums/UserPermissions';
    import {PermissionsForRoster} from '@/types/types';
    import PlanbordGridRow from '@/components/Planbord/PlanbordGridRow.vue';

    @Component({
        components: {
            PlanbordGridRow,
        },
        emits: ['move:resourceRosterRank', 'move:rosterTitleRank'],
    })
    export default class TitleRow extends Vue {
        @Prop() public titleBeforeResource?: Title;
        @Prop({required: true}) public permissions!: PermissionsForRoster;

        public get allowedToReorderUsers(): boolean {
            return this.permissions[UserPermissions.ReorderUsers];
        }

        public openTitleEditModal(): void {
            if (!this.allowedToReorderUsers) {
                return;
            }

            if (!this.titleBeforeResource) {
                console.error('Cannot open title edit modal if there is no title.');

                return;
            }

            /** @see TitleEditModal.open() */
            // @ts-expect-error TS2339
            TitleEditModal.__vfdConstructor.open(this.titleBeforeResource);
        }

        public handleDragDropEvents(event: DragEvent): void {
            if (!this.allowedToReorderUsers) {
                return;
            }

            let target: HTMLDivElement | null = null;

            if (event.target) {
                let temptTarget: HTMLElement = event.target as HTMLElement;
                while (!temptTarget.classList.contains('planbord-grid-row') && temptTarget.parentElement) {
                    temptTarget = temptTarget.parentElement;
                }
                if (temptTarget.classList.contains('planbord-grid-row')) {
                    target = temptTarget as HTMLDivElement;
                }
            }

            if (target === null) {
                return;
            }

            switch (event.type) {
                case 'dragstart':
                    target.style.opacity = '0.5';
                    if (event.dataTransfer) {
                        const dataToTransfer: MoveTitleRankData = {
                            titleId: Number(target.getAttribute('data-id')),
                            currentRank: Number(target.getAttribute('data-rank')),
                        };
                        event.dataTransfer.setData('text', JSON.stringify(dataToTransfer));
                    }
                    break;
                case 'dragend':
                    target.style.opacity = '';
                    break;
                case 'dragover':
                    target.style.opacity = '0.5';
                    break;
                case 'dragleave':
                    target.style.opacity = '';
                    break;
                case 'drop':
                    target.style.opacity = '1';
                    if (event.dataTransfer) {
                        let dataToTransfer: MoveTitleRankData | MoveResourceRankData;
                        try {
                            dataToTransfer = JSON.parse(event.dataTransfer.getData('text'));
                        } catch {
                            event.preventDefault();
                            break;
                        }

                        const newRank: number = Number(target.getAttribute('data-rank'));
                        if (dataToTransfer.currentRank !== newRank) {
                            dataToTransfer.newRank = newRank;
                            if (ResourceRosterRankUtil.isMoveResourceRankData(dataToTransfer)) {
                                this.$emit('move:resourceRosterRank', dataToTransfer);
                            } else if (ResourceRosterRankUtil.isMoveTitleRankData(dataToTransfer)) {
                                this.$emit('move:rosterTitleRank', dataToTransfer);
                            }
                        }
                    }
                    break;
            }
        }
    }
</script>

<style lang="scss" scoped>
    .roster-title {
        --padding-left-right: 0.5rem;
        font-weight: bold;
        padding: 0.2rem var(--padding-left-right) 0.4rem;
        box-shadow: inset 0 1px 0 var(--roster-title-border-color), inset 2px 0 0 var(--roster-title-border-color), inset 0 -3px 1px var(--roster-title-border-color-bottom);
        grid-column: 1 / -1;

        &.editable {
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }

        > span {
            position: sticky;
            left: var(--padding-left-right);
        }
    }
</style>
